/**
 * Wallet address validation utilities for different cryptocurrency networks
 */

/**
 * Validate TRON (TRC20) wallet address
 * TRON addresses start with 'T' and are 34 characters long
 */
export const validateTRONAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return { valid: false, error: 'Invalid address format' };
  }

  const trimmedAddress = address.trim();
  
  // TRON addresses start with 'T' and are 34 characters
  if (!trimmedAddress.startsWith('T')) {
    return { valid: false, error: 'TRON address must start with "T"' };
  }

  if (trimmedAddress.length !== 34) {
    return { valid: false, error: 'TRON address must be 34 characters long' };
  }

  // Check if it contains only valid base58 characters
  const base58Regex = /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;
  if (!base58Regex.test(trimmedAddress)) {
    return { valid: false, error: 'TRON address contains invalid characters' };
  }

  return { valid: true };
};

/**
 * Validate Ethereum (ERC20) wallet address
 * Ethereum addresses start with '0x' and are 42 characters long (40 hex + '0x')
 */
export const validateEthereumAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return { valid: false, error: 'Invalid address format' };
  }

  const trimmedAddress = address.trim();
  
  // Ethereum addresses start with '0x' and are 42 characters
  if (!trimmedAddress.startsWith('0x')) {
    return { valid: false, error: 'Ethereum address must start with "0x"' };
  }

  if (trimmedAddress.length !== 42) {
    return { valid: false, error: 'Ethereum address must be 42 characters long (0x + 40 hex characters)' };
  }

  // Check if it contains only valid hex characters after '0x'
  const hexRegex = /^0x[0-9a-fA-F]{40}$/;
  if (!hexRegex.test(trimmedAddress)) {
    return { valid: false, error: 'Ethereum address contains invalid hex characters' };
  }

  return { valid: true };
};

/**
 * Validate Bitcoin (BTC) wallet address
 * Bitcoin addresses can be in different formats:
 * - Legacy (P2PKH): starts with '1', 26-35 characters
 * - SegWit (P2SH): starts with '3', 26-35 characters
 * - Bech32 (P2WPKH/P2WSH): starts with 'bc1', 42-62 characters
 */
export const validateBitcoinAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return { valid: false, error: 'Invalid address format' };
  }

  const trimmedAddress = address.trim();
  
  // Legacy P2PKH addresses (starts with '1')
  if (trimmedAddress.startsWith('1')) {
    if (trimmedAddress.length < 26 || trimmedAddress.length > 35) {
      return { valid: false, error: 'Bitcoin legacy address must be 26-35 characters long' };
    }
    const base58Regex = /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;
    if (!base58Regex.test(trimmedAddress)) {
      return { valid: false, error: 'Bitcoin address contains invalid characters' };
    }
    return { valid: true };
  }

  // SegWit P2SH addresses (starts with '3')
  if (trimmedAddress.startsWith('3')) {
    if (trimmedAddress.length < 26 || trimmedAddress.length > 35) {
      return { valid: false, error: 'Bitcoin SegWit address must be 26-35 characters long' };
    }
    const base58Regex = /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;
    if (!base58Regex.test(trimmedAddress)) {
      return { valid: false, error: 'Bitcoin address contains invalid characters' };
    }
    return { valid: true };
  }

  // Bech32 addresses (starts with 'bc1')
  if (trimmedAddress.startsWith('bc1')) {
    if (trimmedAddress.length < 42 || trimmedAddress.length > 62) {
      return { valid: false, error: 'Bitcoin Bech32 address must be 42-62 characters long' };
    }
    const bech32Regex = /^bc1[a-z0-9]{39,59}$/i;
    if (!bech32Regex.test(trimmedAddress)) {
      return { valid: false, error: 'Bitcoin Bech32 address contains invalid characters' };
    }
    return { valid: true };
  }

  return { valid: false, error: 'Bitcoin address must start with "1", "3", or "bc1"' };
};

/**
 * Validate wallet address based on network type
 */
export const validateWalletAddress = (address, network) => {
  switch (network) {
    case 'TRON_TRC20':
      return validateTRONAddress(address);
    case 'ETHEREUM_ERC20':
      return validateEthereumAddress(address);
    case 'BTC_BLOCKCHAIN':
      return validateBitcoinAddress(address);
    default:
      return { valid: false, error: 'Invalid network type' };
  }
};

/**
 * Get network display name
 */
export const getNetworkDisplayName = (network) => {
  const networkMap = {
    'TRON_TRC20': 'TRON (TRC20)',
    'ETHEREUM_ERC20': 'Ethereum (ERC20)',
    'BTC_BLOCKCHAIN': 'Bitcoin (BTC)'
  };
  return networkMap[network] || network;
};

/**
 * Get network fee estimate (placeholder - should be fetched from API in production)
 */
export const getNetworkFee = (network) => {
  const feeMap = {
    'TRON_TRC20': 1, // USDT
    'ETHEREUM_ERC20': 5, // USDT (higher gas fees)
    'BTC_BLOCKCHAIN': 0.0001 // BTC
  };
  return feeMap[network] || 0;
};

